import { Card, Flex, Tooltip, Typography } from "antd";
import { AlertOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Column } from "@ant-design/plots";
import type { DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";
import { useGo } from "@refinedev/core";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import dayjs from "dayjs";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 18;
const LEGEND_H    = 24;
const MIN_CHART_H = 80;

/**
 * Sólo cuenta la etapa Seguimiento — Store y Cliente todavía no pasaron el filtro humano del
 * operador, y mezclarlos diluye la señal de qué presupuesto necesita atención (xcons-docs#280).
 */
const ETAPA_CONTEMPLADA = "follow";

/**
 * Las tres barras de D19 (xcons-docs#317), **con techo**: `7` = hasta 7 días sin actividad, `14` =
 * de 8 a 14, `21` = 15 o más. Todo presupuesto en Seguimiento dentro de la ventana cae en alguna.
 * El backend manda los códigos; acá se rotulan y se colorean por severidad.
 */
const BARRAS = ["7", "14", "21"] as const;
type Barra = (typeof BARRAS)[number];

const ETIQUETA_BARRA: Record<Barra, string> = { "7": "7 días", "14": "14 días", "21": "21 días" };
const SEVERIDAD_BARRA: Record<Barra, string> = {
  "7": "Requieren atención",
  "14": "Por entrar en riesgo",
  "21": "En riesgo",
};
const SEVERIDAD_COLORS: Record<string, string> = {
  "Requieren atención": "#faad14",
  "Por entrar en riesgo": "#fa8c16",
  "En riesgo": "#ff4d4f",
};

/**
 * Rango de "última actividad" de cada barra, en días hacia atrás desde hoy, para armar el filtro del
 * listado. **El listado no reproduce exactamente el widget**, y hay que saberlo: acá "sin actividad"
 * incluye la conversación del cliente, y `/quotes` sólo sabe filtrar por `updatedAt` del presupuesto.
 * Así que el listado puede traer alguno de más — los que la conversación rescató.
 */
const RANGO_BARRA: Record<Barra, [number, number]> = {
  "7": [7, 0],
  "14": [14, 8],
  "21": [3650, 15],
};

type DataRow = { band: string; severidad: string; count: number };

type Datos = {
  etapa?: string;
  /** D19: los presupuestos creados entre `desde` y `hasta`, 60 días antes del inicio del período. */
  ventana?: { desde: string; hasta: string };
  barras?: { band: string; count: number; amount: number }[];
  bandas: { band: string; state: string; count: number; amount: number }[];
  total: { count: number; amount: number };
};

const fecha = (iso: string) => dayjs(iso).format("D/M");

export const PresupuestosRiesgoChip = ({
  height = 240,
  period = "day",
  data: metrics,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: { height?: number; period?: DashboardPeriod } & WidgetDataProps<Datos>) => {
  // Siempre las tres barras, aunque valgan cero: el eje tiene que mostrar 7 / 14 / 21 completo.
  const porBarra = new Map<string, number>();
  for (const b of metrics?.barras ?? []) porBarra.set(b.band, b.count);
  if (!metrics?.barras) {
    for (const b of metrics?.bandas ?? []) {
      if (b.state === ETAPA_CONTEMPLADA) porBarra.set(b.band, (porBarra.get(b.band) ?? 0) + b.count);
    }
  }
  const data: DataRow[] = BARRAS.map((band) => ({
    band: ETIQUETA_BARRA[band],
    severidad: SEVERIDAD_BARRA[band],
    count: porBarra.get(band) ?? 0,
  }));
  const hayDatos = data.some((d) => d.count > 0);
  const total = porBarra.get("21") ?? 0;
  const status = estadoDeWidget(estado, error, nuncaCalculado, hayDatos, recargar);

  const go = useGo();
  const { selectedStores } = useSelectedStores();

  /** Click en una barra → el listado de presupuestos en Seguimiento con esa antigüedad y la ventana. */
  const irAlListado = (bandaLabel: string) => {
    const barra = (Object.keys(ETIQUETA_BARRA) as Barra[]).find((k) => ETIQUETA_BARRA[k] === bandaLabel);
    if (!barra) return;

    const [desdeDias, hastaDias] = RANGO_BARRA[barra];
    const filters: any[] = [
      {
        field: "updatedAt",
        operator: "between",
        value: [
          dayjs().subtract(desdeDias, "day").startOf("day").toISOString(),
          dayjs().subtract(hastaDias, "day").endOf("day").toISOString(),
        ],
      },
      { field: "quoteSellers.state", operator: "eq", value: ETAPA_CONTEMPLADA },
    ];
    if (metrics?.ventana) {
      filters.push({
        field: "createdAt",
        operator: "between",
        value: [
          dayjs(metrics.ventana.desde).startOf("day").toISOString(),
          dayjs(metrics.ventana.hasta).endOf("day").toISOString(),
        ],
      });
    }

    const codes = selectedStores.map((s) => s.code);
    if (codes.length) {
      filters.push({ field: "quoteSellers.sellerCode", operator: "in", value: codes });
    }

    go({ to: "/quotes", type: "push", query: { filters } });
  };
  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(MIN_CHART_H, bodyH - PAD_V - LEGEND_H);

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "8px 12px 10px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <AlertOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Presupuestos en riesgo</span>
          <Tooltip
            title="Presupuestos en etapa Seguimiento creados en los 60 días anteriores al inicio del período, agrupados según cuántos días llevan sin actividad hasta hoy: hasta 7, de 8 a 14, y 15 o más. El total del header cuenta sólo los de 21 días."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 260 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={
        <Flex align="center" gap={6}>
          <Text style={{ fontSize: 11, color: "#ff4d4f", fontWeight: 600 }}>{total} en riesgo</Text>
          <Text type="secondary" style={{ fontSize: 10, color: "rgba(0,0,0,0.3)" }}>
            {metrics?.ventana
              ? `creados del ${fecha(metrics.ventana.desde)} al ${fecha(metrics.ventana.hasta)}`
              : "sin actividad a hoy"}
          </Text>
        </Flex>
      }
    >
      <div style={{ height: chartH, overflow: "hidden" }}>
        {status.tipo !== "ok" ? (
          <WidgetPlaceholder status={status} height={chartH} />
        ) : (
        <Column
          data={data}
          xField="band"
          yField="count"
          seriesField="severidad"
          color={({ severidad }: any) => SEVERIDAD_COLORS[severidad] ?? "#d9d9d9"}
          columnStyle={({ severidad }: any) => ({
            fillOpacity: 0.18,
            stroke: SEVERIDAD_COLORS[severidad] ?? "#d9d9d9",
            lineWidth: 1.5,
          })}
          autoFit
          columnWidthRatio={0.55}
          onReady={(plot: any) => {
            plot.on("element:click", (ev: any) => {
              const d = ev?.data?.data;
              if (d?.band) irAlListado(d.band);
            });
            // Sin esto no hay ninguna señal de que se pueda hacer click.
            plot.on("element:mouseenter", () => {
              plot.chart.getCanvas().setCursor("pointer");
            });
            plot.on("element:mouseleave", () => {
              plot.chart.getCanvas().setCursor("default");
            });
          }}
          xAxis={{
            label: { style: { fontSize: 11, fill: "rgba(0,0,0,0.55)" } },
            line: null,
            tickLine: null,
          }}
          yAxis={{
            label: { style: { fontSize: 9, fill: "rgba(0,0,0,0.35)" } },
            grid: { line: { style: { stroke: "#f5f5f5" } } },
            tickCount: 4,
          }}
          tooltip={{
            formatter: (d: any) => ({ name: d.severidad, value: `${d.count} presupuestos` }),
            domStyles: {
              "g2-tooltip": { background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "none", borderRadius: "6px", padding: "8px 10px", color: "rgba(0,0,0,0.85)", fontSize: "12px" },
            },
          }}
          legend={false}
          padding={[4, 8, 22, 8]}
          animation={false}
        />
        )}
      </div>
      <Flex justify="center" align="center" gap={12} style={{ height: LEGEND_H }}>
        {BARRAS.map((band) => (
          <Flex key={band} align="center" gap={4}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: SEVERIDAD_COLORS[SEVERIDAD_BARRA[band]], display: "inline-block" }} />
            <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.55)" }}>{SEVERIDAD_BARRA[band]}</Text>
          </Flex>
        ))}
      </Flex>
    </Card>
  );
};
